// trafficPrefsStore — ADS-B traffic preference state.
//
// Extracted from uiStore.ts as step 2a of HI-2 (audit 2026-05-22).
// The 5 traffic-pref fields + their setters moved here so the
// 895-LOC uiStore can shrink one bucket at a time.
//
// Persistence: reads/writes the shared `fyrefly.ui.v1` localStorage
// blob via the same prefsSync helpers uiStore uses. The localStorage
// key namespace is unchanged so existing users keep their persisted
// traffic preferences after the upgrade.
//
// Cloud-sync: the PersistedUiPrefs schema in prefsSync.ts already
// holds the traffic fields, and pushes go through the same
// schedulePushUiPrefs() debounce. On pull (sign-in), prefsSync.ts
// applies traffic fields to this store. No Supabase schema migration
// needed — the `prefs` jsonb column is schema-less.
//
// Uses subscribeWithSelector middleware so consumers can do fine-grained
// field-level subscriptions (audit synthesis HI-2 goal).

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { loadUiPrefs, persistUiPrefs } from './prefsSync';

// ── Types ────────────────────────────────────────────────────────────

export interface TrafficPrefsState {
  /**
   * Search radius around the ownship for the adsb.fi query.
   * 5–200 nm. Default 40 nm.
   */
  trafficRangeNm: number;

  /**
   * Absolute altitude delta from ownship past which targets are hidden.
   * 0 (off) | 2000 | 5000 | 10000 | 20000 ft.
   * When GPS altitude is unknown the filter is bypassed.
   */
  trafficAltFilterFt: number;

  /**
   * Internet feed cuts out above this MSL altitude (cell coverage thins;
   * stale traffic is dangerous). 0 disables the safety cutoff.
   */
  trafficAutoDisableAboveFt: number;

  /**
   * Hide ground vehicles + parked aircraft (emitter categories C* and
   * B7 + zero-groundspeed targets). Default true.
   */
  trafficHideLowPriority: boolean;

  /**
   * When ON, trafficAltFilterFt only applies once the airborne detector
   * reports we're flying. Ground pre-flight sees the full picture; once
   * airborne the filter narrows to the collision-threat cone.
   * Default ON — right behavior for the 95% case.
   */
  trafficFilterOnlyAirborne: boolean;

  setTrafficRangeNm: (nm: number) => void;
  setTrafficAltFilterFt: (ft: number) => void;
  setTrafficAutoDisableAboveFt: (ft: number) => void;
  setTrafficHideLowPriority: (v: boolean) => void;
  setTrafficFilterOnlyAirborne: (v: boolean) => void;
}

// ── Traffic-prefs migration ladder ───────────────────────────────────
//
// Moved from uiStore.ts where it ran at module load. Runs here at
// trafficPrefsStore module load — before the store reads persisted values.
//
//   v0: original ship defaults (5000 / 5000). Silently hid all
//       cruise traffic on the ground because the filter wasn't
//       gated on the airborne state. Symptom: 'ADS-B is broken'.
//   v1: temporary fix — forced both to 0 to make traffic visible
//       on the ground. Side effect: pilots in cruise had no
//       altitude filter and saw 200+ targets in busy airspace.
//   v2 (current): restore the ±5000 ft default AND introduce
//       trafficFilterOnlyAirborne (default ON), which gates the
//       filter on the airborne detector. Now you see everything
//       on the ground and the threat cone in cruise.
//
// Users still on v0 or v1 are migrated to v2 — we set the alt
// filter back to 5000 and stamp v2.

const TRAFFIC_PREFS_VERSION = 2;
const _prefs = loadUiPrefs();
if ((_prefs.trafficPrefsMigrated ?? 0) < TRAFFIC_PREFS_VERSION) {
  _prefs.trafficAltFilterFt = 5000;
  _prefs.trafficAutoDisableAboveFt = 0;
  _prefs.trafficFilterOnlyAirborne = true;
  _prefs.trafficPrefsMigrated = TRAFFIC_PREFS_VERSION;
  persistUiPrefs({
    trafficAltFilterFt: 5000,
    trafficAutoDisableAboveFt: 0,
    trafficFilterOnlyAirborne: true,
    trafficPrefsMigrated: TRAFFIC_PREFS_VERSION,
  });
}

// ── Store ────────────────────────────────────────────────────────────

export const useTrafficPrefsStore = create<TrafficPrefsState>()(
  subscribeWithSelector((set) => ({
    // Hydrate from the shared prefs blob so the first render already
    // reflects the user's saved choices (no default-then-snap flicker).
    trafficRangeNm: _prefs.trafficRangeNm ?? 40,
    // ±Nft from ownship altitude. Default ±5000 ft — a sensible cone
    // for collision-threat traffic in cruise. The 'only-airborne' gate
    // ensures this is dormant on the ground so a pilot pre-flight still
    // sees every aircraft overhead.
    trafficAltFilterFt: _prefs.trafficAltFilterFt ?? 5000,
    // Default OFF — if cell coverage thins past 5,000 AGL the user can
    // flip it on in Settings. Most fire-aviation ops have continuous LTE /
    // Starlink, so the cutoff would be a regression.
    trafficAutoDisableAboveFt: _prefs.trafficAutoDisableAboveFt ?? 0,
    trafficHideLowPriority: _prefs.trafficHideLowPriority ?? true,
    trafficFilterOnlyAirborne: _prefs.trafficFilterOnlyAirborne ?? true,

    setTrafficRangeNm: (nm) => {
      const clamped = Math.max(5, Math.min(200, Math.round(nm)));
      set({ trafficRangeNm: clamped });
      persistUiPrefs({ trafficRangeNm: clamped });
    },
    setTrafficAltFilterFt: (ft) => {
      const clamped = Math.max(0, Math.min(40_000, Math.round(ft)));
      set({ trafficAltFilterFt: clamped });
      persistUiPrefs({ trafficAltFilterFt: clamped });
    },
    setTrafficAutoDisableAboveFt: (ft) => {
      const clamped = Math.max(0, Math.min(45_000, Math.round(ft)));
      set({ trafficAutoDisableAboveFt: clamped });
      persistUiPrefs({ trafficAutoDisableAboveFt: clamped });
    },
    setTrafficHideLowPriority: (v) => {
      set({ trafficHideLowPriority: v });
      persistUiPrefs({ trafficHideLowPriority: v });
    },
    setTrafficFilterOnlyAirborne: (v) => {
      set({ trafficFilterOnlyAirborne: v });
      persistUiPrefs({ trafficFilterOnlyAirborne: v });
    },
  })),
);
